import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, Validators } from '@angular/forms';
import { SeFeFormService } from 'se-fe-form-service';
import { ChildSaleService } from '../../child-sale.service';
import { TrimPipe } from 'src/shared/pipes/trim.pipe';
import { FormattedDatePipe } from 'src/shared/pipes/formatted-date.pipe';
import { ModalBaseComponent } from '../modal-base.component';
import { take, tap } from 'rxjs/operators';
import { SeFeFormFieldMultiselectComponent } from 'se-fe-form-field-multiselect';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { BulkChildSale } from 'src/shared/models/child-sale.model';
import { ItemVariation } from 'src/shared/models/item-variation.model';
import { dateAtLeastSevenDaysInTheFutureValidator } from 'src/app/validators/date-in-the-past.validator';
import { SeFeExcludedDates } from 'se-fe-datepicker';
import { DateTime } from 'luxon';
import { PriceVariation } from 'src/shared/models/price-variation.model';

export const MAX_BULK_ORDERS = 50;

export type BulkParentSaleInput = number | (Record<string, unknown> & { id: number });

type ParentPaymentMethod = {
  id?: string | number;
  acct_num?: string | number;
};

type ResolvedPaymentMethod = {
  key: string;
  lastFour: string;
};

@Component({
  selector: 'app-bulk-linked-sale-modal',
  styleUrls: ['./bulk-linked-sale-modal.component.scss'],
  templateUrl: './bulk-linked-sale-modal.component.html',
  providers: [TrimPipe, FormattedDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BulkLinkedSaleModalComponent extends ModalBaseComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('creditReason') childSale: SeFeFormFieldMultiselectComponent;
  @Input() parent_sales: BulkParentSaleInput[] = [];
  @Input() processing_fee: string;
  @Input() transaction_fee: string;
  @Input() service_fee_definition_method: string;
  @Input() shared_org_rate: string;
  @Input() shared_org_payment_plan_fee: string;
  @Input() shared_member_rate: string;
  @Input() shared_member_payment_plan_fee: string;
  total: number = 3;

  public excludeStart: string;
  public excludeEnd: string;
  public excludeDates: SeFeExcludedDates[];
  private date: DateTime = DateTime.now();

  public maxOrders = MAX_BULK_ORDERS;
  public exceedsMaxOrders = false;
  private loadedPaymentMethods = new Map<string, ResolvedPaymentMethod>();

  public creditReasonOptions: any = {
    sections: [{ options: [] }],
  };

  public dueDateConfig = {
    label: 'Due Date',
    errorMessages: {
      required: 'Due date is required',
      dateOutOfRange: 'Please choose a date at least 7 days from today',
    },
  };

  public applyCreditConfig = {
    label: 'Apply Credit',
    value: false,
  };

  public creditAmountConfig = {
    label: 'Credit Amount',
    errorMessages: {
      required: 'Please enter a credit amount',
      min: 'Please enter a credit amount greater than zero',
      max: 'Please enter a credit amount that is less than the new order total',
    },
  };

  public creditReasonConfig = {
    label: 'Reason for Credit',
    errorMessages: {
      required: 'Please enter a reason for the credit',
    },
  };

  public descriptionConfig = {
    label: 'Description',
    errorMessages: {
      maxlength: 'Description must be 255 characters or less',
    },
  };

  constructor(
    viewContainerRef: ViewContainerRef,
    detector: ChangeDetectorRef,
    translate: TranslateService,
    fb: FormBuilder,
    el: ElementRef,
    fs: SeFeFormService,
    ChildSaleService: ChildSaleService
  ) {
    super(viewContainerRef, detector, translate, fb, el, fs, ChildSaleService);
    this.createForm();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.fetchCreditReasons();
    this.fetchParentPaymentMethods();
    this.exceedsMaxOrders = this.parent_sales.length > MAX_BULK_ORDERS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parent_sales']) {
      this.exceedsMaxOrders = this.parent_sales.length > MAX_BULK_ORDERS;
      this.fetchParentPaymentMethods();
    }
  }

  protected createForm(): void {
    this.multiselectForm = this.fb.group({
      multiselect: [],
    });
    this.form = this.fb.group({
      textarea: [],
      description: ['', [Validators.maxLength(255)]],
      credit_amount: [],
      credit_reason: [],
      apply_credit: [false, []],
      cardholder_authorization: [false],
      due_date: ['', [Validators.required, dateAtLeastSevenDaysInTheFutureValidator()]],
      items: this.fb.array([]),
    });

    this.excludeStart = this.date.plus({ year: 1 }).toFormat('yyyy-MM-dd');
    this.excludeEnd = this.date.plus({ day: 7 }).toFormat('yyyy-MM-dd');

    this.excludeDates = [{ end: this.excludeEnd }, { start: this.excludeStart }];

    this.form.get('apply_credit').valueChanges.subscribe((value) => {
      const creditAmountControl = this.form.get('credit_amount');
      const creditReasonControl = this.form.get('credit_reason');

      if (value) {
        creditAmountControl.setValidators([
          Validators.required,
          Validators.min(0.01),
          Validators.max(this.getItemsTotal()),
        ]);
        creditReasonControl.setValidators([Validators.required]);
      } else {
        creditAmountControl.reset(null, { emitEvent: false });
        creditReasonControl.reset(null, { emitEvent: false });
        creditAmountControl.clearValidators();
        creditReasonControl.clearValidators();
      }

      creditAmountControl.updateValueAndValidity();
      creditReasonControl.updateValueAndValidity();
    });

    this.form.get('due_date').valueChanges.subscribe(() => {
      const applyCreditControl = this.form.get('apply_credit');
      const creditAmountControl = this.form.get('credit_amount');

      if (applyCreditControl.value) {
        creditAmountControl.setValidators([
          Validators.required,
          Validators.min(0.01),
          Validators.max(this.getItemsTotal()),
        ]);
      } else {
        creditAmountControl.clearValidators();
      }
      creditAmountControl.updateValueAndValidity();
    });

    this.form.get('items').valueChanges.subscribe(() => {
      this.resetPaymentTerms();
    });
  }

  private resetPaymentTerms(): void {
    this.form.get('apply_credit').reset(false);
    this.form.get('due_date').reset('');
  }

  public override getSearchItems(searchItem: string, isUserSearch = true): void {
    this.requestSearchItems(searchItem, isUserSearch, false);
  }

  public onCurrencyInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const valueWithoutSeparators = input.value.replace(/,/g, '');
    const normalizedValue = valueWithoutSeparators.match(/^\d+(?:\.\d{0,2})?/)?.[0] ?? '';

    input.value = normalizedValue;
    this.form.get('credit_amount').setValue(normalizedValue, { emitEvent: false });
  }

  public getItemsTotal(): number {
    const itemsArray = this.getItems();
    return itemsArray.controls.reduce((acc, itemGroup) => {
      const itemValue = itemGroup.get('item')?.value;
      const quantity = itemGroup.get('quantity')?.value;
      if (!itemValue || !quantity) return acc;
      const selectedItem = this.searchItems.find((searchItem) => searchItem.id === Number(itemValue.value));
      if (!selectedItem) return acc;
      return acc + this.getPriceOrPriceVariation(selectedItem) * quantity;
    }, 0);
  }

  private roundMoney(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  private toNumber(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  public getItemAmount(index: number): number {
    const itemGroup = this.getItems().controls[index];
    const itemValue = itemGroup.get('item')?.value;
    const quantity = itemGroup.get('quantity')?.value;
    if (!itemValue || !quantity) return 0;
    const selectedItem = this.searchItems.find((searchItem) => searchItem.id === Number(itemValue.value));
    if (!selectedItem) return 0;
    return this.getPriceOrPriceVariation(selectedItem) * quantity;
  }

  public fetchCreditReasons(): void {
    this.setOrgId();
    this.ChildSaleService.getCreditReasons(this.org_id)
      .pipe(
        take(1),
        tap((response: ApiResponse<{ id: number; title: string }[]>) => {
          this.creditReasonOptions = this.setCreditReasons(response?.result);
        })
      )
      .subscribe({
        error: () => this.detector.detectChanges(),
      });
  }

  public setCreditReasons(reasons: unknown): { sections: { options: { text: string; value: string }[] }[] } {
    const list = Array.isArray(reasons) ? reasons : [];
    const section = list.map((reason: { title: string; id: number }) => ({
      text: reason.title,
      value: reason.id.toString(),
    }));
    return { sections: [{ options: section }] };
  }

  public getSharedPaymentMethodLastFour(): string | null {
    if (this.parent_sales.length !== 1) {
      return null;
    }

    const resolved = this.getResolvedPaymentMethod(this.parent_sales[0]);
    if (resolved?.lastFour) {
      return resolved.lastFour;
    }

    // Fallback when the parent id key does not match the loaded map entry.
    if (this.loadedPaymentMethods.size === 1) {
      return [...this.loadedPaymentMethods.values()][0]?.lastFour ?? null;
    }

    return null;
  }

  public getMaskedSharedPaymentMethodLastFour(): string | null {
    const lastFour = this.getSharedPaymentMethodLastFour();
    return lastFour ? `{****${lastFour}}` : null;
  }

  public getCardholderAuthorizationConfirmationKey(): string {
    if (this.parent_sales.length > 1) {
      return 'CHILD_SALE.BULK.cardholder_authorization_confirmation_multi';
    }

    return this.getSharedPaymentMethodLastFour()
      ? 'CHILD_SALE.BULK.cardholder_authorization_confirmation_single'
      : 'CHILD_SALE.BULK.cardholder_authorization_confirmation_single_generic';
  }

  private fetchParentPaymentMethods(): void {
    this.parent_sales.forEach((parent) => {
      if (typeof parent !== 'number' && this.extractResolvedPaymentMethod(parent)) {
        return;
      }

      const parentId = this.parentSaleId(parent);
      if (!Number.isFinite(parentId)) {
        return;
      }

      this.ChildSaleService.getOrder(parentId)
        .pipe(take(1))
        .subscribe({
          next: (response: ApiResponse<Record<string, unknown>>) => {
            const resolvedPaymentMethod =
              this.extractResolvedPaymentMethod(this.resolveOrderPayload(response)) ??
              this.extractResolvedPaymentMethodFromUnknown(response);

            if (resolvedPaymentMethod) {
              this.loadedPaymentMethods.set(String(parentId), resolvedPaymentMethod);
            }
            this.detector.detectChanges();
          },
          error: () => {
            this.loadedPaymentMethods.delete(String(parentId));
            this.detector.detectChanges();
          },
        });
    });
  }

  private getResolvedPaymentMethod(parent: BulkParentSaleInput): ResolvedPaymentMethod | undefined {
    if (typeof parent !== 'number') {
      const fromParent = this.extractResolvedPaymentMethod(parent);
      if (fromParent) {
        return fromParent;
      }
    }

    return this.loadedPaymentMethods.get(String(this.parentSaleId(parent)));
  }

  private parentSaleId(parent: BulkParentSaleInput): number {
    return typeof parent === 'number' ? Number(parent) : Number(parent?.['id']);
  }

  private extractResolvedPaymentMethod(
    order: Record<string, unknown> | undefined
  ): ResolvedPaymentMethod | undefined {
    if (!order) {
      return undefined;
    }

    const payments = order['payments'] as Array<{ id?: string | number; acct_num?: string | number }> | undefined;
    if (Array.isArray(payments)) {
      for (const payment of payments) {
        const lastFour = this.getLastFour(payment?.acct_num);
        if (lastFour) {
          return {
            key: payment?.id != null ? `payment:${payment.id}` : `acct:${lastFour}`,
            lastFour,
          };
        }
      }
    }

    return this.resolvePaymentMethod(
      order['payment_plan_payment_method'] as ParentPaymentMethod | undefined
    );
  }

  private extractResolvedPaymentMethodFromUnknown(payload: unknown): ResolvedPaymentMethod | undefined {
    if (!payload || typeof payload !== 'object') {
      return undefined;
    }

    const direct = this.extractResolvedPaymentMethod(payload as Record<string, unknown>);
    if (direct) {
      return direct;
    }

    const result = (payload as Record<string, unknown>)['result'];
    if (result && typeof result === 'object') {
      return this.extractResolvedPaymentMethod(result as Record<string, unknown>);
    }

    return undefined;
  }

  private resolveOrderPayload(
    response: ApiResponse<Record<string, unknown>> | Record<string, unknown> | null | undefined
  ): Record<string, unknown> | undefined {
    if (!response || typeof response !== 'object') {
      return undefined;
    }

    const root = response as Record<string, unknown>;
    const result = root['result'];

    if (this.hasAcctNum(result)) {
      return result as Record<string, unknown>;
    }

    if (this.hasAcctNum(root)) {
      return root;
    }

    if (result && typeof result === 'object' && !Array.isArray(result)) {
      return result as Record<string, unknown>;
    }

    return undefined;
  }

  private hasAcctNum(value: unknown): boolean {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const order = value as Record<string, unknown>;
    const payments = order['payments'];
    if (Array.isArray(payments)) {
      return payments.some((payment) => {
        const acctNum = (payment as { acct_num?: string | number } | null)?.acct_num;
        return acctNum != null && String(acctNum).length > 0;
      });
    }

    const paymentMethod = order['payment_plan_payment_method'] as ParentPaymentMethod | null | undefined;
    return paymentMethod?.acct_num != null && String(paymentMethod.acct_num).length > 0;
  }

  private resolvePaymentMethod(paymentMethod: ParentPaymentMethod | undefined): ResolvedPaymentMethod | undefined {
    const lastFour = this.getLastFour(paymentMethod?.acct_num);
    if (!lastFour) {
      return undefined;
    }

    return {
      key: paymentMethod?.id != null ? `id:${paymentMethod.id}` : `acct:${lastFour}`,
      lastFour,
    };
  }

  private getLastFour(accountNumber: string | number | undefined | null): string {
    return accountNumber == null ? '' : String(accountNumber).slice(-4);
  }

  public nextStep(): void {
    if (this.step === 1) {
      const descriptionControl = this.form.get('description');
      descriptionControl.markAsTouched();
      descriptionControl.markAsDirty();
      descriptionControl.updateValueAndValidity();
      if (descriptionControl.invalid) {
        return;
      }
      super.nextStep();
    } else if (this.step !== 2) {
      super.nextStep();
    } else {
      if (this.form.valid) {
        super.nextStep();
      } else {
        const dueDateControl = this.form.get('due_date');
        dueDateControl.markAsTouched();
        dueDateControl.markAsDirty();
        dueDateControl.updateValueAndValidity();

        const applyCredit = this.form.get('apply_credit').value;
        if (applyCredit) {
          const creditAmountControl = this.form.get('credit_amount');
          const creditReasonControl = this.form.get('credit_reason');
          creditAmountControl.markAsDirty();
          creditReasonControl.markAsDirty();
          creditAmountControl.markAsTouched();
          creditReasonControl.markAsTouched();
          creditAmountControl.updateValueAndValidity();
          creditReasonControl.updateValueAndValidity();
        }
      }
    }

    if (this.step === 3 && this.parent_sales.length === 1 && !this.getSharedPaymentMethodLastFour()) {
      this.fetchParentPaymentMethods();
    }
  }

  public getCustomerFeeAmount(): number {
    if (this.getTotal() === 0) return 0;
    let result: number;
    const memberRate = this.toNumber(this.shared_member_rate);
    const memberPlanFee = this.toNumber(this.shared_member_payment_plan_fee);
    const processingFee = this.toNumber(this.processing_fee);
    const transactionFee = this.toNumber(this.transaction_fee);
    switch (this.service_fee_definition_method) {
      case 'shared':
        result = this.getTotal() * memberRate + memberPlanFee;
        break;
      case 'org_pays_recouped':
        result = this.getTotal() * processingFee + transactionFee;
        break;
      case 'org_pays':
      default:
        result = 0;
        break;
    }
    return this.roundMoney(result);
  }

  public getOrganizationFeeAmount(): number {
    if (this.getTotal() === 0) return 0;
    let result: number;
    const orgRate = this.toNumber(this.shared_org_rate);
    const orgPlanFee = this.toNumber(this.shared_org_payment_plan_fee);
    const processingFee = this.toNumber(this.processing_fee);
    const transactionFee = this.toNumber(this.transaction_fee);
    switch (this.service_fee_definition_method) {
      case 'shared':
        result = this.getTotal() * orgRate + orgPlanFee;
        break;
      case 'org_pays_recouped':
        result = 0;
        break;
      case 'org_pays':
      default:
        result = this.getTotal() * processingFee + transactionFee;
        break;
    }
    return this.roundMoney(result);
  }

  public getTotal(): number {
    return this.roundMoney(
      this.itemsSum() - (this.form.get('apply_credit').value ? this.form.get('credit_amount').value : 0)
    );
  }

  public getTotalCharged(): number {
    switch (this.service_fee_definition_method) {
      case 'shared':
      case 'org_pays_recouped':
        return this.getTotal() + this.getCustomerFeeAmount();
      case 'org_pays':
      default:
        return this.getTotal();
    }
  }

  public getTotalForAllOrders(): number {
    return this.roundMoney(this.getTotalCharged() * this.parent_sales.length);
  }

  public submitForm(): void {
    this.submitIsInProgress = true;
    this.ChildSaleService.createBulkOrders(this.prepareBulkChildSale()).subscribe(
      (response) => {
        const succeededCount = response?.result?.succeeded?.length ?? 0;
        if (!response.error || Object.keys(response.error).length === 0) {
          if (succeededCount > 0) {
            this.submit.emit();
            this.cleanForm();
          } else {
            this.error = true;
          }
        } else {
          this.error = true;
        }
        this.submitIsInProgress = false;
      },
      () => {
        this.error = true;
        this.submitIsInProgress = false;
      }
    );
  }

  private parentSaleIdsForRequest(): number[] {
    return this.parent_sales.map((p) => (typeof p === 'number' ? p : Number((p as { id: number }).id)));
  }

  public prepareBulkChildSale(): BulkChildSale {
    const items = this.selectedItems.value.map((item) => ({
      item_variation_id: Number(item.item.value),
      quantity: item.quantity,
    }));
    const creditReasonValue = this.form.get('credit_reason').value?.toString() ?? '';
    const creditReason = this.creditReasonOptions.sections[0].options.find(
      (option) => option.value === creditReasonValue
    );

    return {
      parent_sale_ids: this.parentSaleIdsForRequest(),
      consent_accepted: this.form.get('cardholder_authorization').value,
      child_items: items,
      description: this.form.get('description').value || undefined,
      due_date: DateTime.fromFormat(this.form.get('due_date').value, 'MM/dd/yyyy').toISODate(),
      credit:
        this.form.get('apply_credit').value ?
          {
            amount: parseFloat(this.form.get('credit_amount').value),
            credit_reason: {
              title: creditReason?.text ?? creditReasonValue,
            },
          }
        : null,
      notes: this.form.get('textarea').value,
    };
  }

  public getOrgReceiveAmount(): number {
    return this.roundMoney(this.getTotal() - this.getOrganizationFeeAmount());
  }

  public getOrderDetailUrl(orderId: number): string {
    if (window.location.href.indexOf('/org/') !== -1) {
      const base = window.location.href.split('/org/')[0] + '/org/';
      return base + this.org_id + '/orders/' + orderId + '/detail';
    }
    return '';
  }

  public priceVariationIsActive(item: ItemVariation): boolean {
    return !!this.getMatchingPriceVariation(item);
  }

  protected override getPriceOrPriceVariation(item: ItemVariation): number {
    if (this.form.get('due_date').value) {
      const matchingVariation = this.getMatchingPriceVariation(item);
      return matchingVariation ? matchingVariation.price_cents / 100 : item.price_cents / 100;
    }
    return item.price_cents / 100;
  }

  private getMatchingPriceVariation(item: ItemVariation): Partial<PriceVariation> | undefined {
    const dueDate = new Date(this.form.get('due_date').value);
    return item.price_variations?.find((v) => {
      const start = new Date(v.start_date);
      const end = new Date(v.end_date);
      return dueDate >= start && dueDate <= end;
    });
  }
}
